import { BoardApi } from "../../../services/board-service-api";
import { BoardService } from "./board-service";
import { BoardChangeEvent, IssueCard } from "./types";

// ── Local-first board store ───────────────────────────────────────────────
//
// Holds the board's active working set in memory so the kanban view derives its
// columns/filters/JQL/sort as pure selectors over this set — zero requests on any
// interaction. The set is hydrated once per board via GET /{boardId}/sync, kept live by the
// same BoardService signal that drives every other view (local mutations AND teammates' edits
// over SSE), and reconciled to server truth by a debounced background re-sync for events we
// can't apply surgically (creates, rank changes after a move, restores, subtask counts).
//
// Bounded: if the server reports the working set was truncated, status becomes "fallback" and
// the kanban view keeps using the per-column server queries instead of going fully local.

export type BoardSyncStatus = "idle" | "syncing" | "ready" | "error" | "fallback";

export interface BoardStoreSnapshot {
  // Stable reference between mutations — a fresh array is built only when content changes, so
  // selectors/`useSyncExternalStore` can memoize on it.
  issues: IssueCard[];
  byId: ReadonlyMap<string, IssueCard>;
  status: BoardSyncStatus;
  truncated: boolean;
  // Monotonic counter bumped on every change; a cheap memo key for derived selectors.
  version: number;
}

const RECONCILE_DEBOUNCE_MS: number = 400;

const EMPTY_SNAPSHOT: BoardStoreSnapshot = {
  issues: [],
  byId: new Map<string, IssueCard>(),
  status: "idle",
  truncated: false,
  version: 0
};

class BoardStoreImpl {
  private boardId: string | null = null;
  private byId: Map<string, IssueCard> = new Map();
  private status: BoardSyncStatus = "idle";
  private truncated: boolean = false;
  private version: number = 0;
  private snapshot: BoardStoreSnapshot = EMPTY_SNAPSHOT;
  private listeners: Set<() => void> = new Set();
  private reconcileTimer: ReturnType<typeof setTimeout> | null = null;
  // Guards async syncs against a board switch / newer sync resolving out of order.
  private syncToken: number = 0;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = (): BoardStoreSnapshot => this.snapshot;

  // Point the store at a board and (re)hydrate. A no-op rebuild on the same board still triggers
  // a sync so a remount re-checks server truth.
  open(boardId: string): void {
    if (this.boardId !== boardId) {
      this.boardId = boardId;
      this.byId = new Map();
      this.status = "idle";
      this.truncated = false;
      this.rebuild();
    }
    void this.sync();
  }

  async sync(): Promise<void> {
    const boardId: string | null = this.boardId;
    if (!boardId) return;
    const token: number = ++this.syncToken;
    if (this.byId.size === 0 && this.status !== "syncing") {
      this.status = "syncing";
      this.rebuild();
    }
    try {
      const result = await BoardApi.syncBoard(boardId);
      if (token !== this.syncToken) return; // superseded by a newer sync / board switch
      const next: Map<string, IssueCard> = new Map();
      for (const card of result.items) next.set(card.id, card);
      this.byId = next;
      this.truncated = result.truncated;
      this.status = result.truncated ? "fallback" : "ready";
      this.rebuild();
    } catch {
      if (token !== this.syncToken) return;
      // Keep whatever we have; only surface a hard error on a cold failure.
      this.status = this.byId.size > 0 ? "ready" : "error";
      this.rebuild();
    }
  }

  // Apply a board change event to the in-memory set. Known deltas update the held card instantly;
  // events we can't fully reconstruct schedule a coalesced background re-sync.
  applyEvent(event: BoardChangeEvent): void {
    switch (event.kind) {
      case "issue.updated": {
        const id: string | undefined = event.issueId;
        if (id === undefined) return;
        const current: IssueCard | undefined = this.byId.get(id);
        if (current && event.patch) {
          this.byId.set(id, { ...current, ...event.patch });
          this.rebuild();
        } else {
          // Held without a patch, or an issue that just entered scope → reconcile.
          this.scheduleReconcile();
        }
        return;
      }
      case "issue.moved": {
        const id: string | undefined = event.issueId;
        if (id !== undefined && event.toStatusId) {
          const current: IssueCard | undefined = this.byId.get(id);
          if (current) {
            this.byId.set(id, { ...current, statusId: event.toStatusId });
            this.rebuild();
          }
        }
        // Rank is recomputed server-side on a move; reconcile to fix intra-column ordering.
        this.scheduleReconcile();
        return;
      }
      case "issue.deleted":
      case "issue.archived": {
        const id: string | undefined = event.issueId;
        if (id !== undefined && this.byId.delete(id)) this.rebuild();
        return;
      }
      case "issue.created":
      case "issue.restored":
      case "issue.subtaskChanged":
        // Need full card data we don't carry on the event (or a count change) → reconcile.
        this.scheduleReconcile();
        return;
      default:
        // comment/links/attachments/config changes don't alter the working set's card fields
        // the board renders (mirrors columnShouldReload's "ignore" set).
        return;
    }
  }

  // ── Optimistic primitives (drag-drop / context actions / inline create) ──
  // These mutate the held set instantly; the mutation's emit → reconcile path corrects to
  // server truth (true rank, counts, generated key).

  upsertOptimistic(card: IssueCard): void {
    this.byId.set(card.id, card);
    this.rebuild();
  }

  removeOptimistic(id: string): void {
    if (this.byId.delete(id)) this.rebuild();
  }

  patchOptimistic(id: string, patch: Partial<IssueCard>): void {
    const current: IssueCard | undefined = this.byId.get(id);
    if (current) {
      this.byId.set(id, { ...current, ...patch });
      this.rebuild();
    }
  }

  moveOptimistic(id: string, destStatusId: string, rank: string): void {
    const current: IssueCard | undefined = this.byId.get(id);
    if (current) {
      this.byId.set(id, { ...current, statusId: destStatusId, rank });
      this.rebuild();
    }
  }

  private scheduleReconcile(): void {
    // When the working set is too large to hold locally we're in server-fallback mode; a
    // reconcile would just re-fetch and re-truncate. The columns are reading the server, so
    // there's nothing local to keep fresh.
    if (this.truncated) return;
    if (this.reconcileTimer) clearTimeout(this.reconcileTimer);
    this.reconcileTimer = setTimeout(() => {
      this.reconcileTimer = null;
      void this.sync();
    }, RECONCILE_DEBOUNCE_MS);
  }

  private rebuild(): void {
    this.version += 1;
    this.snapshot = {
      issues: Array.from(this.byId.values()),
      byId: this.byId,
      status: this.status,
      truncated: this.truncated,
      version: this.version
    };
    for (const listener of this.listeners) listener();
  }
}

export const BoardStore: BoardStoreImpl = new BoardStoreImpl();

// The store always listens to board mutations, so its in-memory set stays live regardless of
// which view is mounted. Installed once at module load (mirrors how views subscribe directly).
BoardService.subscribe((event: BoardChangeEvent) => BoardStore.applyEvent(event));
